"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Grid,
  Stack,
  Alert,
  alpha,
  useTheme,
  IconButton,
} from "@mui/material"
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Key as KeyIcon,
} from "@mui/icons-material"
import { createStaff_crm, getStaffList_crm, getStaffList_crm_active } from "../../../api/users/user"
import axios from "axios"
import { requestID, baseUrl, config } from "../../../common/utils"
import { IonPage } from "@ionic/react"

interface Staff {
  id: number
  user_id: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  email: string | null
  loginStatus: number
  status: number
  createdAt: string
  roleName?: string
}

interface NewStaff {
  user_id: string
  firstName: string
  lastName: string
  phone: string
  email: string
  password: string
  role_id?: string
}

interface Role {
  id: string
  roleID: string
  name: string
  description?: string
}

interface ConfirmationDialog {
  open: boolean
  title: string
  message: string
  action: () => void
}

interface EditPasswordData {
  user_id: string
  staffName: string
  newPassword: string
  confirmPassword: string
}

const StaffManagementGeneral: React.FC = () => {
  const theme = useTheme()
  const [staffList, setStaffList] = useState<Staff[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(true)
  const [rolesLoading, setRolesLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [newStaff, setNewStaff] = useState<NewStaff>({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    role_id: "",
  })

  const [editPasswordData, setEditPasswordData] = useState<EditPasswordData>({
    user_id: "",
    staffName: "",
    newPassword: "",
    confirmPassword: "",
  })

  const [confirmDialog, setConfirmDialog] = useState<ConfirmationDialog>({
    open: false,
    title: "",
    message: "",
    action: () => {},
  })

  // Use useEffect with proper cleanup to prevent memory leaks
  useEffect(() => {
    let isMounted = true;
    
    const initializeData = async () => {
      if (isMounted) {
        await fetchStaff();
        await getRoles();
      }
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [])

  const fetchStaff = async () => {
    try {
      setFetchLoading(true)
      setError(null)
      const response = await getStaffList_crm_active()
      const data = await response.data
      
      // Ensure data is an array and filter out any null entries
      const safeData = Array.isArray(data) 
        ? data.filter(staff => staff != null)
        : [];
      
      setStaffList(safeData)
      setSuccess("Staff list updated successfully")
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch staff")
      console.error("Error fetching staff:", err)
    } finally {
      setFetchLoading(false)
    }
  }

  const getRoles = async () => {
    try {
      setRolesLoading(true)
      const formData = new FormData()
      formData.append("requestID", requestID())
      
      const response = await axios.post(
        baseUrl() + "businesss/getRoles_all",
        formData,
        config
      )

      const data = await response.data.data
      // Ensure roles data is safe
      const safeRoles = Array.isArray(data) ? data : [];
      setRoles(safeRoles)
    } catch (err) {
      console.error("Error fetching roles:", err)
      setError("Failed to load roles")
    } finally {
      setRolesLoading(false)
    }
  }

  const suspendStaff = async (dataApi: any) => {
    try {
      const data = {
        requestID: requestID(),
        ...dataApi
      }
      
      const response = await axios.post(
        baseUrl() + "businesss/suspendStaff",
        data,
        config
      )

      console.log({ response })
      fetchStaff()
      setSuccess(`Staff member ${dataApi.action === "activate" ? "activated" : "deactivated"} successfully`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error("Error updating staff status:", err)
      setError("Failed to update staff status")
    }
  }

  // Function to update staff password
  const updateStaffPassword = async (user_id: string, newPassword: string) => {
    try {
      setPasswordLoading(true)
      setError(null)

      const data = {
        requestID: requestID(),
        user_id: user_id,
        new_password: newPassword
      }

      const response = await axios.post(
        baseUrl() + "users/updateStaffPassword",
        data,
        config
      )

      console.log("Password update response:", response)
      
      if (response.data.status) {
        setSuccess("Password updated successfully")
        setTimeout(() => setSuccess(null), 3000)
        return true
      } else {
        throw new Error(response.data.message || "Failed to update password")
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update password"
      setError(errorMessage)
      console.error("Error updating password:", err)
      return false
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleAddStaff = async () => {
    if (!newStaff.firstName || !newStaff.phone || !newStaff.email || !newStaff.password || !newStaff.role_id) {
      setError("Please fill in all required fields")
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await createStaff_crm(newStaff)
      console.log({ response })
      fetchStaff()
      setNewStaff({ 
        firstName: "", 
        lastName: "", 
        phone: "", 
        email: "", 
        password: "",
        role_id: "" 
      })
      setIsModalOpen(false)
      setSuccess("Staff member added successfully")
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add staff")
      console.error("Error adding staff:", err)
    } finally {
      setLoading(false)
    }
  }

  // Function to open edit password dialog
  const handleOpenEditPassword = (staff: Staff) => {
    setEditPasswordData({
      user_id: staff.user_id,
      staffName: getFullName(staff.firstName, staff.lastName),
      newPassword: "",
      confirmPassword: "",
    })
    setIsPasswordModalOpen(true)
  }

  // Function to handle password update
  const handleUpdatePassword = async () => {
    // Validation
    if (!editPasswordData.newPassword) {
      setError("Please enter a new password")
      return
    }

    if (editPasswordData.newPassword !== editPasswordData.confirmPassword) {
      setError("Passwords do not match")
      return
    }

    if (editPasswordData.newPassword.length < 6) {
      setError("Password must be at least 6 characters long")
      return
    }

    const success = await updateStaffPassword(editPasswordData.user_id, editPasswordData.newPassword)
    
    if (success) {
      setIsPasswordModalOpen(false)
      setEditPasswordData({
        user_id: "",
        staffName: "",
        newPassword: "",
        confirmPassword: "",
      })
    }
  }

  // Function to close password dialog
  const handleClosePasswordDialog = () => {
    setIsPasswordModalOpen(false)
    setEditPasswordData({
      user_id: "",
      staffName: "",
      newPassword: "",
      confirmPassword: "",
    })
    setError(null)
  }

  const handleToggleStaffStatus = (staff: Staff) => {
    const isActive = staff.loginStatus === 1
    const action = isActive ? "deactivate" : "activate"
    
    setConfirmDialog({
      open: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Staff Member`,
      message: `Are you sure you want to ${action} ${staff.firstName || ''} ${staff.lastName || ''}?`,
      action: () => confirmToggleStaffStatus(staff, action),
    })
  }

  const confirmToggleStaffStatus = async (staff: Staff, action: string) => {
    const data = {
      staff_id: staff.user_id,
      action: action
    }
    
    try {
      await suspendStaff(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update staff status")
      console.error("Error updating staff status:", err)
    }
    setConfirmDialog({ ...confirmDialog, open: false })
  }

  const handleDeleteStaff = (staff: Staff) => {
    setConfirmDialog({
      open: true,
      title: "Delete Staff Member",
      message: `Are you sure you want to permanently delete ${staff.firstName || ''} ${staff.lastName || ''}? This action cannot be undone.`,
      action: () => confirmDeleteStaff(staff.user_id),
    })
  }

  const confirmDeleteStaff = async (staffId: string) => {
    try {
      // Replace with your actual delete API call
      const response = await fetch(`/api/staff/${staffId}`, {
        method: "DELETE",
      })

      if (!response.ok) {
        throw new Error("Failed to delete staff")
      }

      setStaffList(staffList.filter((staff) => staff.user_id !== staffId))
      setSuccess("Staff member deleted successfully")
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete staff")
      console.error("Error deleting staff:", err)
    }
    setConfirmDialog({ ...confirmDialog, open: false })
  }

  // Helper function to get initials safely
  const getInitials = (firstName: string | null, lastName: string | null): string => {
    const first = firstName?.charAt(0) || ''
    const last = lastName?.charAt(0) || ''
    return (first + last).toUpperCase() || 'U'
  }

  // Helper function to get full name safely
  const getFullName = (firstName: string | null, lastName: string | null): string => {
    return [firstName, lastName].filter(Boolean).join(' ') || 'Unknown User'
  }

  const getStatusDisplay = (loginStatus: number) => {
    return loginStatus === 1 ? "Active" : "Inactive"
  }

  const getStatusColor = (loginStatus: number) => {
    return loginStatus === 1 ? "success" : "error"
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return 'Invalid Date'
    }
  }

  // Helper function to get safe phone number
  const getSafePhone = (phone: string | null): string => {
    return phone || 'No phone'
  }

  // Helper function to get safe email
  const getSafeEmail = (email: string | null): string => {
    return email || 'No email'
  }

  // Generate a unique key for each staff member
  const getStaffKey = (staff: Staff, index: number): string => {
    // Use user_id if available, otherwise create a unique key
    if (staff.user_id && staff.user_id !== 'null') {
      return staff.user_id;
    }
    // Fallback to index with a prefix
    return `staff-${index}-${Date.now()}`;
  }

  return (
    <IonPage className="pushDown">
      <Box sx={{ 
        p: 3, 
        backgroundColor: '#f8f9fa', 
        minHeight: '100vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" component="h1" fontWeight="bold" color="primary">
              Staff Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchStaff}
                disabled={fetchLoading}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    borderColor: theme.palette.primary.dark,
                  }
                }}
              >
                {fetchLoading ? 'Refreshing...' : 'Refresh'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsModalOpen(true)}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }}
              >
                Add Staff
              </Button>
            </Box>
          </Box>
          
          <Typography variant="body1" color="text.secondary">
            Manage your team members and their access permissions
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              backgroundColor: 'primary.main', 
              color: 'white',
              backgroundImage: 'linear-gradient(135deg, #000000 0%, #434343 100%)'
            }}>
              <CardContent>
                <Typography variant="h3" fontWeight="bold">
                  {staffList.length}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Staff Members
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h3" fontWeight="bold" color="success.main">
                  {staffList.filter(staff => staff.loginStatus === 1).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Staff
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h3" fontWeight="bold" color="error.main">
                  {staffList.filter(staff => staff.loginStatus === 0).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inactive Staff
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Staff Table */}
        <Card sx={{ 
          boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)',
          mb: 3 
        }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Staff Members
                </Typography>
                <Chip 
                  label={`${staffList.length} total`} 
                  size="small" 
                  variant="outlined" 
                />
              </Box>
            }
          />
          <CardContent sx={{ p: 0 }}>
            {fetchLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : staffList.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No staff members found. Add your first staff member to get started.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Staff Member</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staffList.map((staff, index) => (
                      <TableRow 
                        key={getStaffKey(staff, index)}
                        hover 
                        sx={{ 
                          '&:last-child td': { borderBottom: 0 },
                          transition: 'background-color 0.2s',
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {getInitials(staff.firstName, staff.lastName)}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" fontWeight="medium">
                                {getFullName(staff.firstName, staff.lastName)}
                              </Typography>
                              {staff.roleName && (
                                <Typography variant="caption" color="text.secondary">
                                  {staff.roleName}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">{getSafePhone(staff.phone)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">{getSafeEmail(staff.email)}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Chip
                            label={getStatusDisplay(staff.loginStatus)}
                            color={getStatusColor(staff.loginStatus)}
                            size="small"
                            variant={staff.loginStatus === 1 ? "filled" : "outlined"}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {formatDate(staff.createdAt)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Edit Password">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditPassword(staff)}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  }
                                }}
                              >
                                <KeyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={staff.loginStatus === 1 ? "Deactivate" : "Activate"}>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={staff.loginStatus === 1 ? <BlockIcon /> : <CheckCircleIcon />}
                                onClick={() => handleToggleStaffStatus(staff)}
                                color={staff.loginStatus === 1 ? "warning" : "success"}
                                sx={{
                                  minWidth: '120px',
                                  fontWeight: 'bold',
                                  color: '#fff',
                                  boxShadow: 3,
                                  textTransform: 'none',
                                }}
                              >
                                {staff.loginStatus === 1 ? "Deactivate" : "Activate"}
                              </Button>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Add Staff Modal */}
        <Dialog 
          open={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: 'primary.main', 
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, #000000 0%, #434343 100%)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon />
              <Typography variant="h6">Add New Staff Member</Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={3}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                value={newStaff.firstName}
                onChange={(e) => setNewStaff({ ...newStaff, firstName: e.target.value })}
                required
              />
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                value={newStaff.lastName}
                onChange={(e) => setNewStaff({ ...newStaff, lastName: e.target.value })}
                required
              />
              <TextField
                label="Phone Number"
                variant="outlined"
                fullWidth
                value={newStaff.phone}
                onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })}
                required
              />
              <TextField
                label="Email Address"
                variant="outlined"
                type="email"
                fullWidth
                value={newStaff.email}
                onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                required
              />
              <TextField
                label="Password"
                variant="outlined"
                type="password"
                fullWidth
                value={newStaff.password}
                onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                required
              />
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newStaff.role_id}
                  onChange={(e) => setNewStaff({ ...newStaff, role_id: e.target.value })}
                  label="Role"
                  disabled={rolesLoading}
                >
                  <MenuItem value="">
                    <em>Select a role</em>
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.roleID} value={role.roleID}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                {rolesLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Loading roles...
                    </Typography>
                  </Box>
                )}
              </FormControl>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setIsModalOpen(false)} 
              color="inherit"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddStaff} 
              variant="contained"
              disabled={loading || !newStaff.role_id}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Adding...
                </Box>
              ) : (
                "Add Staff"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Password Modal */}
        <Dialog 
          open={isPasswordModalOpen} 
          onClose={handleClosePasswordDialog}
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: 'primary.main', 
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, #000000 0%, #434343 100%)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <KeyIcon />
              <Typography variant="h6">
                Change Password for {editPasswordData.staffName}
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Enter a new password for this staff member. The password must be at least 6 characters long.
              </Alert>
              
              <TextField
                label="New Password"
                variant="outlined"
                type="password"
                fullWidth
                value={editPasswordData.newPassword}
                onChange={(e) => setEditPasswordData({ 
                  ...editPasswordData, 
                  newPassword: e.target.value 
                })}
                required
                helperText="Enter the new password"
              />
              
              <TextField
                label="Confirm Password"
                variant="outlined"
                type="password"
                fullWidth
                value={editPasswordData.confirmPassword}
                onChange={(e) => setEditPasswordData({ 
                  ...editPasswordData, 
                  confirmPassword: e.target.value 
                })}
                required
                helperText="Re-enter the new password to confirm"
                error={editPasswordData.newPassword !== editPasswordData.confirmPassword && editPasswordData.confirmPassword !== ""}
              />
              
              {editPasswordData.newPassword !== editPasswordData.confirmPassword && editPasswordData.confirmPassword !== "" && (
                <Alert severity="error">
                  Passwords do not match
                </Alert>
              )}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={handleClosePasswordDialog}
              color="inherit"
              variant="outlined"
              disabled={passwordLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdatePassword}
              variant="contained"
              disabled={
                passwordLoading || 
                !editPasswordData.newPassword || 
                !editPasswordData.confirmPassword ||
                editPasswordData.newPassword !== editPasswordData.confirmPassword ||
                editPasswordData.newPassword.length < 6
              }
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }}
            >
              {passwordLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Updating...
                </Box>
              ) : (
                "Update Password"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {confirmDialog.title}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography>{confirmDialog.message}</Typography> {/* FIXED: Changed confirmPasswordData to confirmDialog */}
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={() => setConfirmDialog({ ...confirmDialog, open: false })} 
              color="inherit"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDialog.action} 
              variant="contained" 
              color="error"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </IonPage>
  )
}

export default StaffManagementGeneral